import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { track } from "@amplitude/analytics-browser";
import { useAuth } from "../../core/auth";
import { useCart } from "../../core/cart";
import Partition from "../../components/common/partition";
import LoginBottomSheet from "../../components/common/login-bottom-sheet";
import { calculateDiscountRate } from "../../utils/price";
import AppDownloadModal from "../../components/app-download/app-download-modal";
import { isWeb } from "../../utils/user-agent";
import { useProductQuery } from "./hooks/use-product-query";
import { useProductRefresh } from "./hooks/use-product-refresh";
import { useProductQuantity } from "./hooks/use-product-quantity";
import { useProductCart } from "./hooks/use-product-cart";
import { useScrollToTopOnChange } from "./hooks/use-scroll-to-top-on-change";
import {
  BusinessInfo,
  ProductImageSection,
  ProductHeader,
  ProductMenuList,
  ProductPriceSection,
  ProductCartCta,
} from "./components";

const Product = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const { addToCart, cart } = useCart();

  const { product, loading, refetch } = useProductQuery(Number(id));
  const { handleRefresh, isRefreshing } = useProductRefresh(refetch);
  const { quantity, increaseQuantity, decreaseQuantity } =
    useProductQuantity(product);
  const { cartQuantityInCart, addProductToCart } = useProductCart(
    product,
    quantity,
    addToCart,
    cart,
    navigate,
  );
  const productContainerRef = useScrollToTopOnChange(loading);

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [showAppDownloadModal, setShowAppDownloadModal] = useState(false);

  const handleCartClick = () => {
    if (isWeb()) {
      setShowAppDownloadModal(true);
    } else {
      track("상품 담기 버튼 클릭", {
        상품: product?.name,
        가격: product?.userPrice,
      });
      if (!isLoggedIn) {
        setIsSheetOpen(true);
      } else {
        addProductToCart();
      }
    }
  };

  return (
    <div
      className="container h-screen overflow-y-scroll mx-auto bg-[#F4F5F7]"
      ref={productContainerRef}
    >
      <div className="relative w-full mx-auto bg-white">
        <ProductImageSection
          product={product}
          loading={loading}
          cartQuantityInCart={cartQuantityInCart}
          onBack={() => navigate(-1)}
        />
        <ProductHeader
          product={product}
          loading={loading}
          isRefreshing={isRefreshing}
          onRefresh={handleRefresh}
        />
      </div>
      <Partition color="light" height="thick" />
      {product?.menus && product.menus.length > 0 && (
        <ProductMenuList menus={product.menus} />
      )}
      <Partition color="light" height="thick" />
      <ProductPriceSection
        product={product}
        loading={loading}
        quantity={quantity}
        onIncrease={increaseQuantity}
        onDecrease={decreaseQuantity}
      />
      <Partition color="light" height="md" />
      <div className="w-full h-80 bg-[#F4F5F7]" />
      <BusinessInfo />
      {product && product.currentCount !== 0 && !product.isEnd && (
        <ProductCartCta
          product={product}
          loading={loading}
          quantity={quantity}
          onClick={handleCartClick}
        />
      )}
      <LoginBottomSheet
        discountRate={
          product && product.price != null && product.price > 0
            ? calculateDiscountRate(
                product.price,
                product.userPrice ?? product.price,
              )
            : "0"
        }
        isOpen={isSheetOpen}
        onClose={() => setIsSheetOpen(false)}
      />
      <AppDownloadModal
        isOpen={showAppDownloadModal}
        onClose={() => setShowAppDownloadModal(false)}
        where="프로덕트 상세 페이지"
      />
    </div>
  );
};

export default Product;
